import { ReactNode, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { IconButton } from "./IconButton";

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
}

const sizes = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-2xl" };

export function Modal({ open, onClose, title, children, footer, size = "md" }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    const handleClose = () => onClose();
    dlg.addEventListener("close", handleClose);
    return () => dlg.removeEventListener("close", handleClose);
  }, [onClose]);

  // Backdrop click
  function onClick(e: React.MouseEvent<HTMLDialogElement>) {
    const dlg = dialogRef.current;
    if (!dlg) return;
    const rect = dlg.getBoundingClientRect();
    const inDialog =
      rect.top <= e.clientY && e.clientY <= rect.bottom &&
      rect.left <= e.clientX && e.clientX <= rect.right;
    if (!inDialog) onClose();
  }

  return (
    <dialog
      ref={dialogRef}
      onClick={onClick}
      className={cn(
        "bg-bg-elevated text-text border border-border rounded-lg shadow-sm",
        "p-0 backdrop:bg-black/40",
        sizes[size],
      )}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h2 className="text-sm font-semibold">{title}</h2>
        <IconButton label="Zamknij" onClick={onClose}><X className="size-4" /></IconButton>
      </div>
      <div className="px-4 py-4 text-sm">{children}</div>
      {footer && <div className="px-4 py-3 border-t border-border flex justify-end gap-2">{footer}</div>}
    </dialog>
  );
}
